import { Component, Inject, OnInit, ViewChild, TemplateRef, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PlayerAuctionService } from '../player-auction.service';
import { ContractCard, CardStatus } from '../player-auction.type';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDivider } from "@angular/material/divider";
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AuthService } from '../../auth/auth.service';
import { CardHistoryDialog } from '../buy-contract/card-history-dialog/card-history-dialog';

export interface ContractCardsDialogData {
  contractId: string;
}

interface CardsGroup {
  key: string;

  items: ContractCard[];
  count: number;
}

@Component({
  selector: 'app-contract-cards-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatTooltipModule,
  ],
  templateUrl: './contract-cards-dialog.html',
  styleUrl: './contract-cards-dialog.scss'
})
export class ContractCardsDialog implements OnInit {
  private static readonly BASE_COLUMNS = ['player', 'buy_now_price', 'cost', 'quality', 'status', 'expire_time'];

  cards: ContractCard[] = [];
  cardGroups: CardsGroup[] = [];
  loading = true;

  @ViewChild('imagePreviewTpl') imagePreviewTpl!: TemplateRef<any>;
  private overlayRef: OverlayRef | null = null;
  previewImageUrl = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ContractCardsDialogData,
    private dialogRef: MatDialogRef<ContractCardsDialog>,
    private service: PlayerAuctionService,
    private snackBar: MatSnackBar,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private dialog: MatDialog,
    private authService: AuthService,
  ) { }

  /** The history button calls an admin-only endpoint. */
  get canShowHistory(): boolean {
    return this.authService.hasAnyRole('Admin', 'SuperAdmin');
  }

  get displayedColumns(): string[] {
    return this.canShowHistory ? [...ContractCardsDialog.BASE_COLUMNS, 'history'] : ContractCardsDialog.BASE_COLUMNS;
  }

  showCardHistory(cardId: string): void {
    this.dialog.open(CardHistoryDialog, {
      data: { cardId },
      autoFocus: false,
    });
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.service.getContractCards(this.data.contractId).subscribe({
      next: cards => {
        this.cards = cards;
        this.groupCards(this.cards);
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Error loading cards', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  groupCards(items: ContractCard[]): void {
    const groupMap = new Map<string, CardsGroup>();

    if (!items || items.length === 0) {
      return;
    }

    items.forEach(item => {
      const key = `${item.player_id}-${item.buy_now_price || 0}-${item.bid_price || 0}-${item.quality}-${item.status}`;

      if (groupMap.has(key)) {
        const group = groupMap.get(key)!;
        group.items.push(item);
        group.count++;
      } else {
        groupMap.set(key, {
          key,
          items: [item],
          count: 1
        });
      }
    });

    this.cardGroups = Array.from(groupMap.values()).sort((a, b) => {
      if (a.items[0].status !== b.items[0].status) {
        return a.items[0].status === 'Listed' ? -1 : 1;
      }
      const aMin = Math.min(...a.items.map(i => new Date(i.expire_time).getTime()));
      const bMin = Math.min(...b.items.map(i => new Date(i.expire_time).getTime()));
      return aMin - bMin;
    });
    return;
  }

  showImagePreview(origin: HTMLElement, imageUrl: string): void {
    this.previewImageUrl = imageUrl;
    const positionStrategy = this.overlay
      .position().global().centerHorizontally().centerVertically();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      hasBackdrop: true,
    });

    this.overlayRef.backdropClick().subscribe(() => this.hideImagePreview());
    this.overlayRef.attach(new TemplatePortal(this.imagePreviewTpl, this.viewContainerRef));
  }

  hideImagePreview(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  formatTimeRemaining(d: string): string {
    if (!d) return '—';
    const diffMs = new Date(d).getTime() - Date.now();
    if (diffMs <= 0) return '-1';
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }

  getCardStatusClass(status: CardStatus): string {
    switch (status) {
      case 'Listed': return 'status-listed';
      case 'Sold': return 'status-sold';
      case 'Expired': return 'status-expired';
      case 'QuickSold': return 'status-quick-sold';
      case 'LeftBehind': return 'status-left-behind';
    }
  }

  formatCoins(n: number): string {
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  }

  countByStatus(status: CardStatus): number {
    return this.cards.filter(c => c.status === status).length;
  }
}
